import {
    Flex,
    Image,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import { ModuleColumnEnum } from 'generated/graphql';
import type {
    ModuleDataColumn,
    PageBuilderModuleMetaProps,
} from '@modules/page-builder/types';
import { PARAGRAPH_PHOTO_WIDTH } from '@modules/page-builder/config/column-constants';
import { DoubleColumnContainer } from '../../common/pagebuilder.style';
import DetailSingleImageShell from '../detail-single-image-shell/DetailSingleImageShell';
import SingleParagraphModule from '../single-paragraph-module/SingleParagraphModule';
import * as S from './ImageParagraphModule.style';
import TagBar from '../../common/tag-bar/TagBar';
import { DetailPageTagBarContainer } from '../../common/pagebuilder.style';
import type { TagType } from '~/modules/product-tagging/type';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import ProductInfo from '@modules/page-builder/component/common/product-info/ProductInfo';

interface ImageParagraphModuleProps extends PageBuilderModuleMetaProps {
    columns: ModuleDataColumn[];
    columnsTags: TagType[];
}

const ImageParagraphModule = ({
    columns,
    columnsTags,
    meta,
}: ImageParagraphModuleProps) => {
    const { css } = useStyles();
    const { mobile } = useAppDisplayModeContext();

    return (
        <>
            <div className={css(DoubleColumnContainer)}>
                {columns.map((column) => {
                    const isImageColumn =
                        column.type === ModuleColumnEnum.ImageColumn;
                    const isTextColumn =
                        column.type === ModuleColumnEnum.TextColumn;

                    if (
                        mobile &&
                        ((!column.imageUrl && isImageColumn) ||
                            (!column.content && isTextColumn))
                    ) {
                        return null;
                    }

                    const imageCol = column.imageUrl ? (
                        <Flex column vAlign="center">
                            <DetailSingleImageShell
                                caption={column.caption}
                                dimension={column.dimension}
                                libraryImage={column?.libraryImage}
                                tags={column.tags}
                                uniqueApprovedTags={column.uniqueApprovedTags}
                                imageURL={column.imageUrl}
                                taggingEnabled={!column.productId}
                                displayReseneTool={!column.productId}
                            >
                                <Image
                                    src={column.imageUrl}
                                    layout={'fill'}
                                    objectFit={'cover'}
                                    width={column.dimension.width}
                                    height={column.dimension.height}
                                    sizes={getImagesSizes(
                                        PARAGRAPH_PHOTO_WIDTH
                                    )}
                                    alt={
                                        column.caption ||
                                        column?.libraryImage?.Title ||
                                        ''
                                    }
                                    variables={{
                                        mixBlendMode: column.productId
                                            ? 'multiply'
                                            : 'normal',
                                    }}
                                />
                            </DetailSingleImageShell>
                        </Flex>
                    ) : undefined;

                    return (
                        <>
                            {isImageColumn &&
                                (column.productId ? (
                                    <ProductInfo
                                        productId={column.productId}
                                        dimension={column.dimension}
                                        variant={'02'}
                                        image={imageCol}
                                        clickable={true}
                                    />
                                ) : (
                                    imageCol
                                ))}
                            {isTextColumn && (
                                <div className={css(S.RichTextWrapper)}>
                                    <SingleParagraphModule
                                        column={column}
                                        meta={meta}
                                    />
                                </div>
                            )}
                        </>
                    );
                })}
            </div>
            {!mobile && columnsTags.length > 0 && (
                <TagBar
                    className={css(DetailPageTagBarContainer)}
                    tags={columnsTags}
                />
            )}
        </>
    );
};

export default ImageParagraphModule;
